import { createElement, useState } from 'react';

import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ControlPosition, MapControl, useMap } from '@vis.gl/react-google-maps';

import { IcClose, IcDefaultMap, IcMap, IcSateliteMap } from '@/assets/images';

import { useStyles } from './sharedMapStyles';

export const MAP_TYPES: {
  key: string;
  label: string;
  value: 'DEFAULT' | 'SATELLITE';
  icon: React.ElementType;
}[] = [
  {
    key: 'default',
    label: 'Default',
    value: 'DEFAULT',
    icon: IcDefaultMap,
  },
  {
    key: 'satellite',
    label: 'Satellite',
    value: 'SATELLITE',
    icon: IcSateliteMap,
  },
];

export type MapTypeControlProps = {
  position?: ControlPosition;
};

export const MapTypeControl = ({
  position = ControlPosition.LEFT_BOTTOM,
}: MapTypeControlProps) => {
  const classes = useStyles();
  const map = useMap();

  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [selectedType, setSelectedType] = useState<'DEFAULT' | 'SATELLITE'>(
    'DEFAULT',
  );
  const [selectedSubType, setSelectedSubType] = useState<{
    terrain: boolean;
    labels: boolean;
  }>({
    terrain: false,
    labels: false,
  });

  const handleTypeChange = (value: 'DEFAULT' | 'SATELLITE') => {
    setSelectedType(value);
    map?.setMapTypeId(
      value === 'DEFAULT'
        ? selectedSubType.terrain
          ? 'terrain'
          : 'roadmap'
        : selectedSubType.labels
        ? 'hybrid'
        : 'satellite',
    );
  };

  const handleTerrainChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSubType((prev) => ({ ...prev, terrain: event.target.checked }));
    map?.setMapTypeId(event.target.checked ? 'terrain' : 'roadmap');
  };

  const handleLabelsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSubType((prev) => ({ ...prev, labels: event.target.checked }));
    map?.setMapTypeId(event.target.checked ? 'hybrid' : 'satellite');
  };

  const renderButton = () => {
    return (
      <IconButton
        className={`${classes.controllButton} `}
        onClick={handleClick}
        id="map-type-control-button"
        aria-controls={open ? 'map-type-control-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <IcMap />
      </IconButton>
    );
  };

  return (
    <MapControl position={position}>
      <Box sx={{ ml: 1, mb: 1 }}>
        <div>{renderButton()}</div>
      </Box>
      <Menu
        sx={{ ml: 1, my: 0 }}
        id="map-type-control-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'map-type-control-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight={500} fontSize={14} sx={{ ml: 1 }}>
            Map Types
          </Typography>
          <IconButton onClick={handleClose}>
            <IcClose color={theme.palette.error.main} />
          </IconButton>
        </Stack>
        {MAP_TYPES.map(({ key, label, icon, value }) => (
          <MenuItem
            key={key}
            sx={{ px: 1, py: 0 }}
            onClick={() => {
              handleTypeChange(value);
            }}
          >
            <Stack
              direction="row"
              width="100%"
              height="35px"
              alignItems="center"
              justifyContent="flex-start"
            >
              {createElement(icon, {
                style: {
                  height: 24,
                  width: 24,
                  marginRight: 8,
                  border: `2px solid`,
                  borderColor:
                    selectedType === value
                      ? `${theme.palette.error.main}`
                      : 'transparent',
                  borderRadius: 4,
                },
              })}
              <Typography
                fontSize={14}
                fontWeight={500}
                color={
                  selectedType === value
                    ? theme.palette.text.primary
                    : theme.palette.text.disabled
                }
              >
                {label}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
        <Box sx={{ px: 1, py: 0 }}>
          <FormControlLabel
            checked={selectedSubType.terrain}
            disabled={selectedType !== 'DEFAULT'}
            control={
              <Checkbox
                size="small"
                checked={selectedSubType.terrain}
                onChange={handleTerrainChange}
              />
            }
            label="Terrain"
          />
          <FormControlLabel
            checked={selectedSubType.labels}
            disabled={selectedType !== 'SATELLITE'}
            control={
              <Checkbox
                size="small"
                checked={selectedSubType.labels}
                onChange={handleLabelsChange}
              />
            }
            label="Labels"
          />
        </Box>
      </Menu>
    </MapControl>
  );
};
